import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Link2, MousePointerClick, Target, Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function TrackingPage() {
  const [links, setLinks] = useState([]);
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ url: '', product_id: '' });

  const fetchData = useCallback(async () => {
    try {
      const [l, s, p] = await Promise.all([api.get('/tracking/links'), api.get('/tracking/stats'), api.get('/products')]);
      setLinks(l.data.items);
      setStats(s.data);
      setProducts(p.data.items);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    try {
      await api.post('/tracking/links', form);
      toast.success('Link utworzony');
      setDialogOpen(false);
      setForm({ url: '', product_id: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/api/t/${code}`;
    navigator.clipboard.writeText(url);
    toast.success('Link skopiowany');
  };

  return (
    <div className="space-y-6" data-testid="tracking-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Tracking</h1>
          <p className="text-sm text-muted-foreground mt-1">Linki afiliacyjne i sledzenie konwersji</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="tracking-add-button"><Plus className="h-4 w-4 mr-2" />Nowy link</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="border-border/50 bg-card/80">
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground uppercase tracking-wide">Linki</p><p className="text-2xl font-semibold font-heading tabular-nums mt-1">{stats.total_links || 0}</p></div>
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Link2 className="h-5 w-5 text-primary" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50 bg-card/80">
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground uppercase tracking-wide">Klikniecia</p><p className="text-2xl font-semibold font-heading tabular-nums mt-1">{stats.total_clicks || 0}</p></div>
              <div className="w-10 h-10 rounded-lg bg-[hsl(var(--chart-2))]/10 flex items-center justify-center"><MousePointerClick className="h-5 w-5 text-[hsl(var(--chart-2))]" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50 bg-card/80">
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground uppercase tracking-wide">Konwersje</p><p className="text-2xl font-semibold font-heading tabular-nums mt-1">{stats.total_conversions || 0}</p></div>
              <div className="w-10 h-10 rounded-lg bg-[hsl(var(--success))]/10 flex items-center justify-center"><Target className="h-5 w-5 text-[hsl(var(--success))]" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50 bg-card/80">
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground uppercase tracking-wide">CTR</p><p className="text-2xl font-semibold font-heading tabular-nums mt-1">{stats.conversion_rate || 0}%</p></div>
              <div className="w-10 h-10 rounded-lg bg-[hsl(var(--chart-3))]/10 flex items-center justify-center"><ExternalLink className="h-5 w-5 text-[hsl(var(--chart-3))]" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border/50 bg-card/80">
        <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Linki afiliacyjne</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div> : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Kod</TableHead>
                  <TableHead>URL docelowy</TableHead>
                  <TableHead>Klikniecia</TableHead>
                  <TableHead>Konwersje</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="w-20">Kopiuj</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {links.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Brak linkow. Utworz pierwszy!</TableCell></TableRow>
                ) : links.map(l => (
                  <TableRow key={l.id} className="hover:bg-muted/30 transition-colors duration-150">
                    <TableCell className="font-mono text-sm text-primary">{l.short_code}</TableCell>
                    <TableCell className="text-sm text-muted-foreground truncate max-w-xs">{l.original_url}</TableCell>
                    <TableCell className="font-mono text-sm tabular-nums">{l.click_count}</TableCell>
                    <TableCell className="font-mono text-sm tabular-nums">{l.conversion_count}</TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground">{l.created_at?.slice(0, 10)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyLink(l.short_code)}><Copy className="h-3.5 w-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Nowy link afiliacyjny</DialogTitle><DialogDescription>Utworz link ze sledzeniem klikniec</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>URL docelowy</Label><Input value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://..." data-testid="tracking-form-url" /></div>
            <div className="space-y-2">
              <Label>Produkt (opcjonalnie)</Label>
              <Select value={form.product_id} onValueChange={v => setForm({...form, product_id: v})}>
                <SelectTrigger><SelectValue placeholder="Wybierz produkt" /></SelectTrigger>
                <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>Anuluj</Button>
            <Button onClick={handleCreate} disabled={!form.url} data-testid="tracking-form-submit">Utworz link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
